// @ts-nocheck
import React from 'react';
import { BarChart3, FileSpreadsheet, Layers, Scale, ArrowRightLeft, GitCompare, Clock, Trash2, Star, Sparkles, ListChecks, Wand2, ChevronRight, Landmark, Building2, BookOpen } from 'lucide-react';
import { useTenantConfig } from '../../contexts';
import ConfirmDeleteButton from './ConfirmDeleteButton';
import './Sidebar.css';

const MAPPER_RECENT_LIMIT = 5;
const AGENT_RECENT_LIMIT = 5;

const Sidebar = ({
  activePage,
  onPageChange,
  savedSessions = [],
  sessionsLoading = false,
  mapperSessions = [],
  mapperSessionsLoading = false,
  agentSessions = [],
  onLoadSession,
  onLoadAgentSession,
  onDeleteSession,
  onDeleteAgentSession,
  onClearAllAgentSessions,
  onPinMapperSession,
  onUnpinMapperSession,
  loadedSessionId,
  currentAgentSessionId,
  currentBalanceSheetSessionId,
  onLoadBalanceSheetSession,
  currentTBToBSSessionId,
  onLoadTBToBSSession,
  currentUnifiedSessionId,
  onLoadUnifiedSession,
  currentDatabookSessionId,
  onLoadDatabookSession,
  userEmail,
  onOpenAccount,
}) => {
  const { hasFeature, featuresLoading } = useTenantConfig();

  // Check if user has any tools - show Tools section only if they have at least one
  const hasMapper = hasFeature('mapper');
  const hasComparative = hasFeature('comparative');
  const hasMerger = hasFeature('merger');
  const hasRecon = hasFeature('recon');
  const hasTBConverter = hasFeature('tb_converter');
  const hasAgent = hasFeature('agent');
  // Balance Sheet is a separate, layered add-on: it runs on the agent
  // infrastructure but is gated independently so an agent-only tenant (e.g. a
  // newly onboarded member) doesn't see it.
  const hasBalanceSheet = hasFeature('balance_sheet');
  // Trial Balance → Balance Sheet: gated strictly on the dedicated tb_bs feature.
  const hasTBBS = hasFeature('tb_bs');
  // Databook: gated on the dedicated databook feature alone, so it counts as
  // a tool in its own right (it may be the only one a tenant has).
  const hasDatabook = hasFeature('databook');
  const hasAnyTool = hasMapper || hasComparative || hasMerger || hasRecon || hasTBConverter || hasAgent || hasBalanceSheet || hasTBBS || hasDatabook;

  // Don't show tools section while loading features
  const showToolsSection = !featuresLoading && hasAnyTool;

  const visibleSessions = savedSessions.slice(0, 10);

  // Split mapper sessions into pinned (saved) and recent (rolling buffer ≤ 5)
  const pinnedMapperSessions = mapperSessions.filter((s) => s.is_pinned);
  const recentMapperSessions = mapperSessions
    .filter((s) => !s.is_pinned)
    .slice(0, MAPPER_RECENT_LIMIT);

  // Split the shared agent-sessions list by statement_type into the P&L agent
  // flow, the isolated balance-sheet flow, the isolated TB→BS flow, and the
  // unified (P&L + BS) flow. Unified PARENTS carry session_kind='unified' on
  // file_refs[0]; their two child sessions carry parent_session_id and are
  // hidden from every list (they're internal to the unified run). Databook
  // parents (session_kind='databook') are split out the same way; their
  // children carry parent_session_id too, so the same check hides them.
  const firstRef = (s) => (Array.isArray(s.file_refs) ? s.file_refs : [])[0];
  const isUnifiedParent = (s) => firstRef(s)?.session_kind === 'unified';
  const isUnifiedChild = (s) => Boolean(firstRef(s)?.parent_session_id);
  const isDatabookParent = (s) => firstRef(s)?.session_kind === 'databook';
  const isBalanceSheet = (s) => firstRef(s)?.statement_type === 'bs';
  const isTBToBS = (s) => firstRef(s)?.statement_type === 'tb_bs';
  const recentAgentSessions = (agentSessions || [])
    .filter(
      (s) =>
        !isBalanceSheet(s) &&
        !isTBToBS(s) &&
        !isUnifiedParent(s) &&
        !isUnifiedChild(s) &&
        !isDatabookParent(s),
    )
    .slice(0, AGENT_RECENT_LIMIT);
  const recentBalanceSheetSessions = (agentSessions || [])
    .filter(
      (s) =>
        isBalanceSheet(s) &&
        !isUnifiedParent(s) &&
        !isUnifiedChild(s) &&
        !isDatabookParent(s),
    )
    .slice(0, AGENT_RECENT_LIMIT);
  const recentTBBSSessions = (agentSessions || [])
    .filter((s) => isTBToBS(s) && !isUnifiedChild(s) && !isDatabookParent(s))
    .slice(0, AGENT_RECENT_LIMIT);
  const recentUnifiedSessions = (agentSessions || [])
    .filter(isUnifiedParent)
    .slice(0, AGENT_RECENT_LIMIT);
  const recentDatabookSessions = (agentSessions || [])
    .filter(isDatabookParent)
    .slice(0, AGENT_RECENT_LIMIT);

  // These groups share the unified "Sessions" umbrella below. The umbrella
  // only renders when at least one has something to show; dividers only
  // render between adjacent non-empty groups.
  const hasMapperContent =
    hasMapper &&
    (pinnedMapperSessions.length > 0 || recentMapperSessions.length > 0);
  const hasAgentContent = hasAgent && recentAgentSessions.length > 0;
  const hasBalanceSheetContent = hasBalanceSheet && recentBalanceSheetSessions.length > 0;
  const hasTBBSContent = hasTBBS && recentTBBSSessions.length > 0;
  const hasUnified = hasFeature('unified_agent');
  const hasUnifiedContent = hasUnified && recentUnifiedSessions.length > 0;
  const hasDatabookContent = hasDatabook && recentDatabookSessions.length > 0;

  // Agent sessions don't carry a user-set name; we synthesize one from the
  // first uploaded file. Fall back to the session id when no file is attached.
  const agentSessionLabel = (s) => {
    const refs = Array.isArray(s.file_refs) ? s.file_refs : [];
    const name = refs[0]?.filename;
    if (name) return name.replace(/\.xlsx?$/i, '');
    return s.id ? `Session ${s.id.slice(0, 6)}` : 'Untitled session';
  };

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  };

  // Agent sessions show a full timestamp — month, day, year, and the time
  // with AM/PM — rather than the compact "May 25" the mapper/comparative
  // lists use. A running or just-finished agent run is something you check
  // the exact time of, often at a glance from your phone. created_at /
  // updated_at are timestamptz, so the ISO string carries an offset and
  // new Date() renders correct local time.
  const formatAgentDateTime = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return '';
    }
  };

  return (
    <aside className="sidebar">
      <nav className="sidebar-nav">
        {showToolsSection && (
          <div className="sidebar-section">
            <div className="sidebar-section-title">Tools</div>
            {hasAgent && (
              <button
                className={`sidebar-item ${activePage === 'agent' && !currentAgentSessionId ? 'active' : ''}`}
                onClick={() => onPageChange('agent')}
                title="Conversational mapping agent"
              >
                <Wand2 size={18} />
                <span>Agent</span>
              </button>
            )}
            {hasBalanceSheet && (
              <button
                className={`sidebar-item ${activePage === 'balance-sheet' && !currentBalanceSheetSessionId ? 'active' : ''}`}
                onClick={() => onPageChange('balance-sheet')}
                title="Balance-sheet mapping agent"
              >
                <Landmark size={18} />
                <span>Balance Sheet</span>
              </button>
            )}
            {hasTBBS && (
              <button
                className={`sidebar-item ${activePage === 'tb-to-bs' && !currentTBToBSSessionId ? 'active' : ''}`}
                onClick={() => onPageChange('tb-to-bs')}
                title="Convert a trial balance into a balance sheet"
              >
                <Building2 size={18} />
                <span>Trial Balance → Balance Sheet</span>
              </button>
            )}
            {hasUnified && (
              <button
                className={`sidebar-item ${activePage === 'unified' && !currentUnifiedSessionId ? 'active' : ''}`}
                onClick={() => onPageChange('unified')}
                title="Map a P&L and balance sheet together and unlock the working capital cycle"
              >
                <Layers size={18} />
                <span>Full Statements</span>
              </button>
            )}
            {hasDatabook && (
              <button
                className={`sidebar-item ${activePage === 'databook' && !currentDatabookSessionId ? 'active' : ''}`}
                onClick={() => onPageChange('databook')}
                title="Map management accounts into your databook template"
              >
                <BookOpen size={18} />
                <span>Databook</span>
              </button>
            )}
            {hasMapper && (
              <button
                className={`sidebar-item ${activePage === 'mapper' && !loadedSessionId ? 'active' : ''}`}
                onClick={() => onPageChange('mapper')}
              >
                <BarChart3 size={18} />
                <span>Mapper</span>
              </button>
            )}
            {hasComparative && (
              <button
                className={`sidebar-item ${activePage === 'comparative' && !loadedSessionId ? 'active' : ''}`}
                onClick={() => onPageChange('comparative')}
              >
                <GitCompare size={18} />
                <span>Comparative</span>
              </button>
            )}
            {hasMerger && (
              <button
                className={`sidebar-item ${activePage === 'merger' ? 'active' : ''}`}
                onClick={() => onPageChange('merger')}
              >
                <Layers size={18} />
                <span>Merger</span>
              </button>
            )}
            {hasRecon && (
              <button
                className={`sidebar-item ${activePage === 'recon' ? 'active' : ''}`}
                onClick={() => onPageChange('recon')}
              >
                <Scale size={18} />
                <span>Recon</span>
              </button>
            )}
            {hasTBConverter && (
              <button
                className={`sidebar-item ${activePage === 'tb-converter' ? 'active' : ''}`}
                onClick={() => onPageChange('tb-converter')}
              >
                <ArrowRightLeft size={18} />
                <span>TB to P&L</span>
              </button>
            )}
            {hasMapper && (
              <button
                className={`sidebar-item ${activePage === 'qoe-workbench' && !loadedSessionId ? 'active' : ''}`}
                onClick={() => onPageChange('qoe-workbench')}
                title="Review QoE flagged adjustments and build the Adjusted EBITDA bridge"
              >
                <ListChecks size={18} />
                <span>QoE Workbench</span>
              </button>
            )}
          </div>
        )}

        {(hasMapperContent || hasAgentContent || hasBalanceSheetContent || hasTBBSContent || hasUnifiedContent || hasDatabookContent) && (
          <div className="sidebar-section">
            <div className="sidebar-section-title">Sessions</div>

            {hasMapperContent && (
              <div className="sidebar-subgroup">
                <div className="sidebar-subgroup-title">Mapper</div>

                {pinnedMapperSessions.map((s) => (
                  <div
                    key={s.id}
                    className={`sidebar-session-item ${loadedSessionId === s.id ? 'active' : ''}`}
                    onClick={() => onLoadSession && onLoadSession(s.id, 'mapping')}
                    title={s.name}
                  >
                    <button
                      className="session-pin-btn pinned"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUnpinMapperSession && onUnpinMapperSession(s.id);
                      }}
                      title="Unpin session"
                    >
                      <Star size={14} fill="currentColor" />
                    </button>
                    <div className="session-info">
                      <span className="session-name">{s.name}</span>
                      <span className="session-date">
                        {formatDate(s.created_at)}
                        {s.has_qoe_report && <Sparkles size={11} className="session-qoe-badge" title="QoE attached" />}
                      </span>
                    </div>
                    <button
                      className="session-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteSession && onDeleteSession(s.id, 'mapping');
                      }}
                      title="Delete session"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}

                {recentMapperSessions.map((s, idx) => {
                  const isLast = idx === recentMapperSessions.length - 1 && recentMapperSessions.length === MAPPER_RECENT_LIMIT;
                  return (
                    <div
                      key={s.id}
                      className={`sidebar-session-item ${loadedSessionId === s.id ? 'active' : ''}`}
                      onClick={() => onLoadSession && onLoadSession(s.id, 'mapping')}
                      title={isLast ? 'Will be removed on next mapping unless saved' : s.name}
                    >
                      <button
                        className="session-pin-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onPinMapperSession && onPinMapperSession(s.id, null);
                        }}
                        title="Save (pin) session"
                      >
                        <Star size={14} />
                      </button>
                      <div className="session-info">
                        <span className="session-name">{s.name}</span>
                        <span className="session-date">
                          {formatDate(s.created_at)}
                          {s.has_qoe_report && <Sparkles size={11} className="session-qoe-badge" title="QoE attached" />}
                          {isLast && <span className="session-eviction-hint"> · next-evict</span>}
                        </span>
                      </div>
                      <button
                        className="session-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteSession && onDeleteSession(s.id, 'mapping');
                        }}
                        title="Delete session"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {hasMapperContent && hasAgentContent && (
              <div className="sidebar-subgroup-divider" aria-hidden="true" />
            )}

            {hasAgentContent && (
              <div className="sidebar-subgroup">
                <div className="sidebar-subgroup-title sidebar-subgroup-title-with-action">
                  <span>Agent</span>
                  {onClearAllAgentSessions && (
                    <ConfirmDeleteButton
                      trigger="Clear all"
                      triggerClassName="sidebar-clear-all-btn"
                      triggerTitle="Delete every agent session"
                      question={`Delete all ${recentAgentSessions.length}?`}
                      confirmLabel="Delete all"
                      onConfirm={onClearAllAgentSessions}
                    />
                  )}
                </div>

                {recentAgentSessions.map((s) => (
                  <div
                    key={s.id}
                    className={`sidebar-session-item ${
                      activePage === 'agent' && currentAgentSessionId === s.id
                        ? 'active'
                        : ''
                    }`}
                    onClick={() =>
                      onLoadAgentSession && onLoadAgentSession(s.id)
                    }
                    title={agentSessionLabel(s)}
                  >
                    <div className="session-info session-info-no-pin">
                      <span className="session-name">{agentSessionLabel(s)}</span>
                      <span className="session-date">
                        <span className="agent-session-time">
                          {formatAgentDateTime(s.updated_at || s.created_at)}
                        </span>
                        {s.status === 'active' && (
                          <span
                            className="agent-session-running-pill"
                            title="Agent run in progress"
                          >
                            <span className="agent-session-running-dot" />
                            running
                          </span>
                        )}
                        {s.status === 'failed' && (
                          <span className="agent-session-fail-pill" title="Run failed">
                            failed
                          </span>
                        )}
                      </span>
                    </div>
                    {onDeleteAgentSession && (
                      <ConfirmDeleteButton
                        trigger={<Trash2 size={13} />}
                        triggerClassName="session-delete-btn"
                        triggerTitle="Delete session"
                        question="Delete this session?"
                        onConfirm={() => onDeleteAgentSession(s.id)}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {(hasMapperContent || hasAgentContent) && hasBalanceSheetContent && (
              <div className="sidebar-subgroup-divider" aria-hidden="true" />
            )}

            {hasBalanceSheetContent && (
              <div className="sidebar-subgroup">
                <div className="sidebar-subgroup-title">
                  <span>Balance Sheet</span>
                </div>

                {recentBalanceSheetSessions.map((s) => (
                  <div
                    key={s.id}
                    className={`sidebar-session-item ${
                      activePage === 'balance-sheet' &&
                      currentBalanceSheetSessionId === s.id
                        ? 'active'
                        : ''
                    }`}
                    onClick={() =>
                      onLoadBalanceSheetSession && onLoadBalanceSheetSession(s.id)
                    }
                    title={agentSessionLabel(s)}
                  >
                    <div className="session-info session-info-no-pin">
                      <span className="session-name">{agentSessionLabel(s)}</span>
                      <span className="session-date">
                        <span className="agent-session-time">
                          {formatAgentDateTime(s.updated_at || s.created_at)}
                        </span>
                        {s.status === 'active' && (
                          <span
                            className="agent-session-running-pill"
                            title="Agent run in progress"
                          >
                            <span className="agent-session-running-dot" />
                            running
                          </span>
                        )}
                        {s.status === 'failed' && (
                          <span className="agent-session-fail-pill" title="Run failed">
                            failed
                          </span>
                        )}
                      </span>
                    </div>
                    {onDeleteAgentSession && (
                      <ConfirmDeleteButton
                        trigger={<Trash2 size={13} />}
                        triggerClassName="session-delete-btn"
                        triggerTitle="Delete session"
                        question="Delete this session?"
                        onConfirm={() => onDeleteAgentSession(s.id)}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {(hasMapperContent || hasAgentContent || hasBalanceSheetContent) && hasTBBSContent && (
              <div className="sidebar-subgroup-divider" aria-hidden="true" />
            )}

            {hasTBBSContent && (
              <div className="sidebar-subgroup">
                <div className="sidebar-subgroup-title">
                  <span>Trial Balance → BS</span>
                </div>

                {recentTBBSSessions.map((s) => (
                  <div
                    key={s.id}
                    className={`sidebar-session-item ${
                      activePage === 'tb-to-bs' &&
                      currentTBToBSSessionId === s.id
                        ? 'active'
                        : ''
                    }`}
                    onClick={() =>
                      onLoadTBToBSSession && onLoadTBToBSSession(s.id)
                    }
                    title={agentSessionLabel(s)}
                  >
                    <div className="session-info session-info-no-pin">
                      <span className="session-name">{agentSessionLabel(s)}</span>
                      <span className="session-date">
                        <span className="agent-session-time">
                          {formatAgentDateTime(s.updated_at || s.created_at)}
                        </span>
                        {s.status === 'active' && (
                          <span
                            className="agent-session-running-pill"
                            title="Agent run in progress"
                          >
                            <span className="agent-session-running-dot" />
                            running
                          </span>
                        )}
                        {s.status === 'failed' && (
                          <span className="agent-session-fail-pill" title="Run failed">
                            failed
                          </span>
                        )}
                      </span>
                    </div>
                    {onDeleteAgentSession && (
                      <ConfirmDeleteButton
                        trigger={<Trash2 size={13} />}
                        triggerClassName="session-delete-btn"
                        triggerTitle="Delete session"
                        question="Delete this session?"
                        onConfirm={() => onDeleteAgentSession(s.id)}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {(hasMapperContent ||
              hasAgentContent ||
              hasBalanceSheetContent ||
              hasTBBSContent) &&
              hasUnifiedContent && (
                <div className="sidebar-subgroup-divider" aria-hidden="true" />
              )}

            {hasUnifiedContent && (
              <div className="sidebar-subgroup">
                <div className="sidebar-subgroup-title">
                  <span>Full Statements</span>
                </div>

                {recentUnifiedSessions.map((s) => (
                  <div
                    key={s.id}
                    className={`sidebar-session-item ${
                      activePage === 'unified' &&
                      currentUnifiedSessionId === s.id
                        ? 'active'
                        : ''
                    }`}
                    onClick={() =>
                      onLoadUnifiedSession && onLoadUnifiedSession(s.id)
                    }
                    title={agentSessionLabel(s)}
                  >
                    <div className="session-info session-info-no-pin">
                      <span className="session-name">{agentSessionLabel(s)}</span>
                      <span className="session-date">
                        <span className="agent-session-time">
                          {formatAgentDateTime(s.updated_at || s.created_at)}
                        </span>
                        {s.status === 'active' && (
                          <span
                            className="agent-session-running-pill"
                            title="Unified run in progress"
                          >
                            <span className="agent-session-running-dot" />
                            running
                          </span>
                        )}
                        {s.status === 'failed' && (
                          <span className="agent-session-fail-pill" title="Run failed">
                            failed
                          </span>
                        )}
                      </span>
                    </div>
                    {onDeleteAgentSession && (
                      <ConfirmDeleteButton
                        trigger={<Trash2 size={13} />}
                        triggerClassName="session-delete-btn"
                        triggerTitle="Delete session and its two statement runs"
                        question="Delete this session?"
                        onConfirm={() => onDeleteAgentSession(s.id)}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {(hasMapperContent ||
              hasAgentContent ||
              hasBalanceSheetContent ||
              hasTBBSContent ||
              hasUnifiedContent) &&
              hasDatabookContent && (
                <div className="sidebar-subgroup-divider" aria-hidden="true" />
              )}

            {hasDatabookContent && (
              <div className="sidebar-subgroup">
                <div className="sidebar-subgroup-title">
                  <span>Databook</span>
                </div>

                {recentDatabookSessions.map((s) => (
                  <div
                    key={s.id}
                    className={`sidebar-session-item ${
                      activePage === 'databook' &&
                      currentDatabookSessionId === s.id
                        ? 'active'
                        : ''
                    }`}
                    onClick={() =>
                      onLoadDatabookSession && onLoadDatabookSession(s.id)
                    }
                    title={agentSessionLabel(s)}
                  >
                    <div className="session-info session-info-no-pin">
                      <span className="session-name">{agentSessionLabel(s)}</span>
                      <span className="session-date">
                        <span className="agent-session-time">
                          {formatAgentDateTime(s.updated_at || s.created_at)}
                        </span>
                        {s.status === 'active' && (
                          <span
                            className="agent-session-running-pill"
                            title="Databook run in progress"
                          >
                            <span className="agent-session-running-dot" />
                            running
                          </span>
                        )}
                        {s.status === 'failed' && (
                          <span className="agent-session-fail-pill" title="Run failed">
                            failed
                          </span>
                        )}
                      </span>
                    </div>
                    {onDeleteAgentSession && (
                      <ConfirmDeleteButton
                        trigger={<Trash2 size={13} />}
                        triggerClassName="session-delete-btn"
                        triggerTitle="Delete session and its two statement runs"
                        question="Delete this session?"
                        onConfirm={() => onDeleteAgentSession(s.id)}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {hasComparative && visibleSessions.length > 0 && (
          <div className="sidebar-section">
            <div className="sidebar-section-title">Previous Comparative Mappings</div>
            {visibleSessions.map((s) => (
              <div
                key={s.id}
                className={`sidebar-session-item ${loadedSessionId === s.id ? 'active' : ''}`}
                onClick={() => onLoadSession && onLoadSession(s.id, 'comparative')}
                title={s.name}
              >
                <Clock size={14} className="session-icon" />
                <div className="session-info">
                  <span className="session-name">{s.name}</span>
                  <span className="session-date">{formatDate(s.created_at)}</span>
                </div>
                <button
                  className="session-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteSession && onDeleteSession(s.id, 'comparative');
                  }}
                  title="Delete session"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="sidebar-section">
          <div className="sidebar-section-title">Config</div>
          <button
            className={`sidebar-item ${activePage === 'chart-of-accounts' ? 'active' : ''}`}
            onClick={() => onPageChange('chart-of-accounts')}
          >
            <FileSpreadsheet size={18} />
            <span>Chart of Accounts</span>
          </button>
        </div>
      </nav>

      <button
        type="button"
        className={`sidebar-account-banner ${activePage === 'account' ? 'active' : ''}`}
        onClick={() => (onOpenAccount ? onOpenAccount() : onPageChange('account'))}
        title="Open account settings"
        aria-label="Open account settings"
      >
        <span className="sidebar-account-avatar" aria-hidden="true">
          {accountInitials(userEmail)}
        </span>
        <span className="sidebar-account-text">
          <span className="sidebar-account-label">My account</span>
          <span className="sidebar-account-email">{userEmail || 'Not signed in'}</span>
        </span>
        <ChevronRight size={14} className="sidebar-account-chevron" />
      </button>
    </aside>
  );
};

const accountInitials = (email) => {
  if (!email) return '?';
  const local = String(email).split('@')[0] || '';
  const parts = local.split(/[._\-+]/).filter(Boolean);
  const first = parts[0]?.[0] ?? local[0] ?? '?';
  const second = parts.length > 1 ? parts[1]?.[0] : '';
  return (first + (second || '')).toUpperCase();
};

export default Sidebar;
